import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { post } from 'services/api';
import { postToast } from 'features/common/utils/postFlash';

import styles from './PunchupBoxOffice.module.css';

interface NewPromotionFormProps {
	eventId: number;
	onCreated: (code: string) => void;
	onCancel: () => void;
	disabled?: boolean;
}

type DiscountKind = 'percent' | 'flat';

// Quick-create a single-use promo for this sale. Mirrors the new promotion system (POST /promotions):
// active status, applied per-order, no end date, and a max_uses_per_code=1 rule scoped to this event so
// the code only applies to its tickets and burns after one order.
const generateCode = (): string => `BOX${Math.random().toString(36).slice(2, 7).toUpperCase().padEnd(5, '0')}`;

export const NewPromotionForm: FC<NewPromotionFormProps> = ({ eventId, onCreated, onCancel, disabled }) => {
	const { t } = useTranslation();
	const [name, setName] = useState('');
	const [discountKind, setDiscountKind] = useState<DiscountKind>('percent');
	const [amount, setAmount] = useState('');
	const [code, setCode] = useState(generateCode);

	const { mutate: createPromotion, isLoading } = useMutation({
		mutationFn: (): Promise<unknown> =>
			post('/promotions', {
				name: name.trim(),
				discount_type: discountKind === 'percent' ? 'percent' : 'flat_rate',
				amount: Number(amount),
				status: 'active',
				start_date: new Date().toISOString(),
				end_date: null,
				applied: 'event_specific',
				codes: [{ code: code.trim() }],
				rules: [{ rule_type: 'max_uses_per_code', rule_value: 1, event_id: eventId }],
			}),
		onSuccess: () => {
			postToast('success', t('boxOffice-promoCreated', 'Promo code created'));
			onCreated(code.trim());
		},
		onError: () => {
			postToast(
				'error',
				t('boxOffice-promoCreateFailed', 'Could not create promo code'),
				t('boxOffice-promoCreateFailedHint', 'Check the details and your permissions, then try again.')
			);
		},
	});

	const amountNum = Number(amount);
	const amountValid =
		amount.trim() !== '' &&
		!Number.isNaN(amountNum) &&
		amountNum > 0 &&
		(discountKind === 'percent' ? amountNum <= 100 : true);
	const canSubmit = name.trim() !== '' && code.trim() !== '' && amountValid && !disabled && !isLoading;

	return (
		<div className={styles.newPromo}>
			<div className={styles.field}>
				<label htmlFor="punchup-box-office-new-promo-name" className={styles.label}>
					{t('boxOffice-newPromoName', 'Promotion name')}
				</label>
				<input
					id="punchup-box-office-new-promo-name"
					type="text"
					className={styles.input}
					placeholder={t('boxOffice-newPromoNamePlaceholder', 'e.g. Door staff discount')}
					value={name}
					onChange={(e) => setName(e.target.value)}
					disabled={disabled || isLoading}
				/>
			</div>

			<div className={styles.newPromoRow}>
				<div className={styles.field}>
					<span className={styles.label}>{t('boxOffice-newPromoType', 'Discount type')}</span>
					<div className={styles.toggleRow} role="radiogroup" aria-label={t('boxOffice-newPromoType', 'Discount type')}>
						<button
							type="button"
							role="radio"
							aria-checked={discountKind === 'percent'}
							className={discountKind === 'percent' ? styles.toggleOptionActive : styles.toggleOption}
							onClick={() => setDiscountKind('percent')}
							disabled={disabled || isLoading}
						>
							{t('boxOffice-newPromoPercentLabel', 'Percent')}
						</button>
						<button
							type="button"
							role="radio"
							aria-checked={discountKind === 'flat'}
							className={discountKind === 'flat' ? styles.toggleOptionActive : styles.toggleOption}
							onClick={() => setDiscountKind('flat')}
							disabled={disabled || isLoading}
						>
							{t('boxOffice-newPromoFlatLabel', 'Amount')}
						</button>
					</div>
				</div>
				<div className={styles.field}>
					<label htmlFor="punchup-box-office-new-promo-amount" className={styles.label}>
						{discountKind === 'percent'
							? t('boxOffice-newPromoPercentOff', 'Percent off')
							: t('boxOffice-newPromoAmountOff', 'Amount off')}
					</label>
					<input
						id="punchup-box-office-new-promo-amount"
						type="number"
						min="0"
						className={styles.input}
						placeholder={discountKind === 'percent' ? '10' : '5'}
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						disabled={disabled || isLoading}
					/>
				</div>
			</div>

			<div className={styles.field}>
				<label htmlFor="punchup-box-office-new-promo-code" className={styles.label}>
					{t('boxOffice-newPromoCode', 'Code')}
				</label>
				<input
					id="punchup-box-office-new-promo-code"
					type="text"
					className={styles.input}
					value={code}
					onChange={(e) => setCode(e.target.value.toUpperCase())}
					disabled={disabled || isLoading}
					autoComplete="off"
				/>
			</div>

			<div className={styles.newPromoActions}>
				<PunchupButton variant="outline" type="button" onClick={onCancel} disabled={isLoading}>
					{t('cancel', 'Cancel')}
				</PunchupButton>
				<PunchupButton
					variant="primary"
					type="button"
					onClick={() => createPromotion()}
					isLoading={isLoading}
					disabled={!canSubmit}
				>
					{t('boxOffice-newPromoCreate', 'Create & select')}
				</PunchupButton>
			</div>
		</div>
	);
};
